import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button, Input } from '@wove/web-ui'
import { X, Settings, TestTube, CheckCircle, XCircle } from 'lucide-react'
import { CargoWiseSettings, UpdateCargoWiseSettingsRequest, useTestCargoWiseConnection } from '@wove/api'

const cargoWiseSettingsSchema = z.object({
  credentials: z.object({
    username: z.string()
      .min(1, 'Username is required')
      .max(100, 'Username must be 100 characters or less'),
    password: z.string()
      .min(1, 'Password is required')
      .max(200, 'Password must be 200 characters or less'),
    eAdaptorUrl: z.string()
      .min(1, 'eAdaptor URL is required')
      .url('eAdaptor URL must be a valid URL')
  })
})

type FormData = z.infer<typeof cargoWiseSettingsSchema>

interface CargoWiseSettingsFormProps {
  settings?: CargoWiseSettings | null
  onSave: (data: UpdateCargoWiseSettingsRequest) => void
  onCancel: () => void
  isSubmitting: boolean
  integrationId: string
}

export default function CargoWiseSettingsForm({
  settings,
  onSave,
  onCancel,
  isSubmitting,
  integrationId,
}: CargoWiseSettingsFormProps) {
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const testConnectionMutation = useTestCargoWiseConnection(integrationId)
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(cargoWiseSettingsSchema),
    defaultValues: {
      credentials: {
        username: settings?.credentials?.username || '',
        password: settings?.credentials?.password || '',
        eAdaptorUrl: settings?.credentials?.eAdaptorUrl || '',
      },
    },
  })

  const onFormSubmit = (data: FormData) => {
    onSave(data)
  }

  const handleTestConnection = async () => {
    setTestResult(null)
    
    // Get current form values
    const formData = getValues()
    
    try {
      const result = await testConnectionMutation.mutateAsync({
        credentials: formData.credentials
      })
      setTestResult(result)
    } catch (error: any) {
      const resultObj = error.response?.data
      setTestResult({
        success: false,
        message: resultObj?.message || 'Connection test failed. Please try again later.',
      })
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Settings className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {settings ? 'Edit CargoWise Settings' : 'Configure CargoWise Settings'}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">CargoWise eAdaptor Connection</h4>
            <p className="text-sm text-blue-700">
              These credentials will be used to connect to your CargoWise eAdaptor service. 
              Make sure the user has the necessary permissions to access the required data.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username *
              </label>
              <Controller
                name="credentials.username"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={isSubmitting}
                    placeholder="e.g., NaviaUser"
                    className={errors.credentials?.username ? 'border-red-300' : ''}
                  />
                )}
              />
              {errors.credentials?.username && (
                <p className="mt-1 text-sm text-red-600">{errors.credentials.username.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Your CargoWise username for eAdaptor access
              </p>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <Controller
                name="credentials.password"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="password"
                    disabled={isSubmitting}
                    placeholder="••••••••••••"
                    className={errors.credentials?.password ? 'border-red-300' : ''}
                  />
                )}
              />
              {errors.credentials?.password && (
                <p className="mt-1 text-sm text-red-600">{errors.credentials.password.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Your CargoWise password for eAdaptor access
              </p>
            </div>
          </div>

          {/* eAdaptor URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              eAdaptor URL *
            </label>
            <Controller
              name="credentials.eAdaptorUrl"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  disabled={isSubmitting}
                  placeholder="https://NLPTS1services.wisegrid.net/eAdaptor"
                  className={errors.credentials?.eAdaptorUrl ? 'border-red-300' : ''}
                />
              )}
            />
            {errors.credentials?.eAdaptorUrl && (
              <p className="mt-1 text-sm text-red-600">{errors.credentials.eAdaptorUrl.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              The full URL to your CargoWise eAdaptor service endpoint
            </p>
          </div>

          {/* Security Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-yellow-900 mb-2">Security Notice</h4>
            <p className="text-sm text-yellow-700">
              Your credentials are encrypted and stored securely. They will only be used to 
              establish connections with your CargoWise system and will not be shared with 
              any third parties.
            </p>
          </div>

          {/* Test Connection */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Test Connection</h4>
                <p className="text-sm text-gray-500">
                  Verify your credentials before saving
                </p>
              </div>
              <Button
                type="button"
                onClick={handleTestConnection}
                disabled={testConnectionMutation.isPending || isSubmitting}
                variant="outline"
                className="flex items-center gap-2"
              >
                <TestTube className="h-4 w-4" />
                {testConnectionMutation.isPending ? 'Testing...' : 'Test Connection'}
              </Button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`mt-3 p-3 rounded-md flex items-center ${
                testResult.success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-400 mr-2" />
                )}
                <span className={`text-sm ${
                  testResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {testResult.message}
                </span>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : settings ? 'Update Settings' : 'Save Settings'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}